import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { AppName } from 'sl-api-connector/types';
import NumberFormat from 'react-number-format';
import _pick from 'lodash/pick';
import _cloneDeep from 'lodash/cloneDeep';
import _orderBy from 'lodash/orderBy';
import _get from 'lodash/get';

import { getEmptyFormData } from 'src/utils/segments';
import { AdManagerEvents } from 'src/types/application/event';
import ReduxStore from 'src/types/store/reduxStore';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import CampaignColumn from 'src/components/AdManager/Search/CustomColumns/EntityColumn';
import DataColumn from 'src/components/AdManager/Search/CustomColumns/DataColumn';
import { useBus } from 'src/utils/Hooks';
import { withBus } from 'react-bus';
import { store } from 'src/main';
import { fetchEntityMetrics } from 'src/store/modules/entitySearchService/operations';
import { GridLoading } from 'src/components/common/Loading/PlaceHolderLoading/PlaceHolderLoading';
import CustomAgMaterial from 'src/components/Grids/Data/CustomAgMaterial';
import { AdCampaignManagerServiceClient } from 'src/utils/adCampaignManagerServiceClient';
import {
  buildAggregations,
  buildTimePeriodRangeFilters,
  zipMetricsResponseIntoArray
} from 'src/components/AdManager/Search';
import CustomAgGridHeaderTemplate from 'src/components/EntityGrid/Table/CustomAgGridHeaderTemplate';

const style = {
  button: {
    width: 99
  },
  textInput: {
    height: '32px',
    outline: 'none',
    border: 'none',
    borderRadius: '5px',
    width: '80px',
    padding: 10,
    fontSize: '14px',
    color: 'currentColor',
    background: '#eff1f5',
    fontFamily: 'Roboto'
  }
};

const onGridReady = (params: any) => {
  params.api.sizeColumnsToFit();
  params.api.refreshHeader();
};

const isRangeFilterNotEqual = (
  rangeFilter1: { minValue?: number; maxValue?: number },
  rangeFilter2: { minValue?: number; maxValue?: number }
) => {
  return (
    `${rangeFilter1.minValue}` !== `${rangeFilter2.minValue}` ||
    `${rangeFilter1.maxValue}` !== `${rangeFilter2.maxValue}`
  );
};

const buildTermFilterCondition = ({
  idFieldName,
  filterValues,
  condition
}: {
  idFieldName: string;
  filterValues: { i: string; n?: string }[];
  condition?: 'should' | 'must' | 'must_not';
}) => {
  const termFilters = [];
  if (filterValues.length > 0) {
    termFilters.push({
      fieldName: idFieldName,
      values: filterValues.map((value) => value.i),
      condition
    });
  }
  return termFilters;
};

const terms = [
  { filterName: 'entityId', fieldName: 'entityId' },
  { filterName: 'portfolioId', fieldName: 'portfolioId' },
  { filterName: 'campaignId', fieldName: 'campaignId' },
  { filterName: 'campaignType', fieldName: 'campaignType' },
  { filterName: 'keyword', fieldName: 'keyword' },
  { filterName: 'excludedKeyword', fieldName: 'excludedKeyword' },
  { filterName: 'campaignProductState', fieldName: 'status' }
];

const ranges = [
  { filterName: 'spendComputed', fieldName: 'spendComputed' },
  { filterName: 'costPerClick', fieldName: 'costPerClick' },
  { filterName: 'returnOnAdSpend', fieldName: 'returnOnAdSpend' },
  { filterName: 'bid', fieldName: 'cpcMinBidAmount' }
];

const getMinBid = (data, adPlatformSettings) => {
  let campaignType = _get(data, ['entity', 'extendedAttributes', 'childDocuments', 0, 'campaignType'], null);

  if (!campaignType) {
    campaignType = _get(data, ['entity', 'campaignType'], null);
  }
  const match = adPlatformSettings.find((set) => _get(set, ['extendedAttributes', 'campaignType']) === campaignType);

  if (match) {
    return _get(match, ['extendedAttributes', 'productLimits', 'minimumBid'], 0.02);
  }

  // fallback logic
  return campaignType === 'sponsoredBrands' ? 0.1 : 0.02;
};

const getMaxBid = (data, adPlatformSettings) => {
  let campaignType = _get(data, ['entity', 'extendedAttributes', 'childDocuments', 0, 'campaignType'], null);

  if (!campaignType) {
    campaignType = _get(data, ['entity', 'campaignType'], null);
  }

  const match = adPlatformSettings.find((set) => _get(set, ['extendedAttributes', 'campaignType']) === campaignType);

  if (match) {
    return _get(match, ['extendedAttributes', 'productLimits', 'maximumBid'], 0.1);
  }

  // fallback logic
  return campaignType === 'sponsoredBrands' ? 49 : 1000;
};

const mapStateToPropsForColumn = (state: ReduxStore) => {
  const { retailer, adPlatformSettings } = state;
  return {
    retailer,
    adPlatformSettings
  };
};

const EditBitColumn = connect(mapStateToPropsForColumn)(
  withBus('eventBus')(({ eventBus, value, data, retailer, adPlatformSettings }) => {
    const apiLimits = adPlatformSettings.filter((set) => set.settingType === 'apiLimit');
    const minBid = getMinBid(data, apiLimits);
    const maxBid = getMaxBid(data, apiLimits);
    return (
      <NumberFormat
        value={value}
        isAllowed={({ floatValue }) => {
          return floatValue >= minBid && floatValue <= maxBid; // Amazon's rule, all bid price must be greater or equal to $0.02
        }}
        thousandSeparator
        prefix={retailer.currencySymbol}
        decimalScale={2}
        fixedDecimalScale
        allowNegative={false}
        onValueChange={({ floatValue }) =>
          eventBus.emit('handleCampaignBidPriceChange', {
            key: data.id,
            value: floatValue
          })
        }
        // Mimic the style of the MUI `TextInput`
        style={style.textInput}
      />
    );
  })
);

const mapStateToProps = (state: ReduxStore) => {
  return {
    ..._pick(state, ['app', 'retailer', 'mainTimePeriod', 'comparisonTimePeriod', 'adPlatformSettings']),
    mainEntity: state.entityService.mainEntity
  };
};

const ExistingTargets: React.FC<ReturnType<typeof mapStateToProps> & { adGroupId: any; eventBus: any }> = ({
  eventBus,
  app,
  retailer,
  mainTimePeriod,
  comparisonTimePeriod,
  mainEntity,
  adGroupId,
  adPlatformSettings
}) => {
  const [isLoading, setLoading] = useState(true);
  const [submitStatus, setSubmitStatus] = useState({ status: 'normal' });
  const [dataSet, setDataSet] = useState([]);
  const [formData, setFormData] = useState({});
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortFieldName, setSortFieldName] = useState('clicks');
  const [bidMapping, setBidMapping] = useState(new Map());

  const handleBidChange = ({ key, value }: { key: string; value: any }) => {
    // bidMapping.set(id, value);
    const newBidMapping = new Map(bidMapping);
    newBidMapping.set(key, value);
    setBidMapping(newBidMapping);
  };

  const apiLimits = adPlatformSettings.filter((set) => set.settingType === 'apiLimit');

  const handleBulkBidChange = ({ type, value }: { type: string; value: any }) => {
    if (type === 'increaseMoney') {
      const newDataSet = _cloneDeep(dataSet);
      newDataSet.forEach((dataPoint) => {
        const originalBid = _get(dataPoint, ['originalBid']);
        let newBid = originalBid + (value || 0);
        const minBid = getMinBid(dataPoint, apiLimits);
        const maxBid = getMaxBid(dataPoint, apiLimits);
        newBid = newBid > maxBid ? maxBid : newBid < minBid ? minBid : newBid;
        dataPoint.bid = +newBid.toFixed(2);
      });
      setBidMapping(new Map());
      setDataSet(newDataSet);
    } else if (type === 'increasePercent') {
      const newDataSet = _cloneDeep(dataSet);
      newDataSet.forEach((dataPoint) => {
        const originalBid = _get(dataPoint, ['originalBid']);
        let newBid = originalBid * (1 + (value || 0) * 0.01);
        const minBid = getMinBid(dataPoint, apiLimits);
        const maxBid = getMaxBid(dataPoint, apiLimits);
        newBid = newBid > maxBid ? maxBid : newBid < minBid ? minBid : newBid;
        dataPoint.bid = +newBid.toFixed(2);
      });
      setBidMapping(new Map());
      setDataSet(newDataSet);
    } else if (type === 'setMoney') {
      const newDataSet = _cloneDeep(dataSet);
      newDataSet.forEach((dataPoint) => {
        const originalBid = _get(dataPoint, ['originalBid']);
        let newBid = value || value === 0 ? value : originalBid;
        const minBid = getMinBid(dataPoint, apiLimits);
        const maxBid = getMaxBid(dataPoint, apiLimits);
        newBid = newBid > maxBid ? maxBid : newBid < minBid ? minBid : newBid;
        dataPoint.bid = +newBid.toFixed(2);
      });
      setBidMapping(new Map());
      setDataSet(newDataSet);
    } else if (type === 'decreaseMoney') {
      const newDataSet = _cloneDeep(dataSet);
      newDataSet.forEach((dataPoint) => {
        const originalBid = _get(dataPoint, ['originalBid']);
        let newBid = originalBid - (value || 0);
        const minBid = getMinBid(dataPoint, apiLimits);
        const maxBid = getMaxBid(dataPoint, apiLimits);
        newBid = newBid > maxBid ? maxBid : newBid < minBid ? minBid : newBid;
        dataPoint.bid = +newBid.toFixed(2);
      });
      setBidMapping(new Map());
      setDataSet(newDataSet);
    } else if (type === 'decreasePercent') {
      const newDataSet = _cloneDeep(dataSet);
      newDataSet.forEach((dataPoint) => {
        const originalBid = _get(dataPoint, ['originalBid']);
        let newBid = originalBid * (1 - (value || 0) * 0.01);
        const minBid = getMinBid(dataPoint, apiLimits);
        const maxBid = getMaxBid(dataPoint, apiLimits);
        newBid = newBid > maxBid ? maxBid : newBid < minBid ? minBid : newBid;
        dataPoint.bid = +newBid.toFixed(2);
      });
      setBidMapping(new Map());
      setDataSet(newDataSet);
    }
  };

  useEffect(() => {
    eventBus.emit('targetPageButtonStatus', submitStatus);
  }, [eventBus, submitStatus]);

  const submit = async () => {
    setSubmitStatus({ status: 'submitting' });
    const entitiesSelected = dataSet
      .filter((dataPoint: any) => {
        const originalBid = +dataPoint.originalBid;
        let newBid = dataPoint.bid;
        if (
          bidMapping.has(dataPoint.id) &&
          bidMapping.get(dataPoint.id) &&
          bidMapping.get(dataPoint.id) !== dataPoint.bid
        ) {
          newBid = bidMapping.get(dataPoint.id);
        }
        dataPoint.originalBid = newBid;
        dataPoint.entity.extendedAttributes = { bid: newBid };
        return `${newBid}` !== `${originalBid}`;
      })
      .map(({ entity }) => entity);
    if (entitiesSelected && entitiesSelected.length > 0) {
      const { beaconClientId } = entitiesSelected.filter((x: any) => x.beaconClientId)[0];
      const { platformType } = entitiesSelected.filter((x: any) => x.platformType)[0];
      try {
        const res = await AdCampaignManagerServiceClient.updateProductBid({
          beaconClientId,
          platformType,
          entities: entitiesSelected,
          entityType: entitiesSelected[0].type
        });
        if (res) {
          if (res.status === 200) {
            setSubmitStatus({ status: 'submitted', result: res });
          } else {
            setSubmitStatus({ status: 'failure', result: res });
          }
        } else {
          setSubmitStatus({ status: 'failure', result: res });
        }
      } catch (err) {
        setSubmitStatus({ status: 'failure' });
      }
    }
  };

  const sideBarFilterUpdated = ({ formData: newFormData }: { formData: any }) => {
    setFormData(newFormData);
  };

  useBus(eventBus, 'handleCampaignBidPriceChange', handleBidChange);
  useBus(eventBus, 'bulkApplyProductBidPrice', handleBulkBidChange);
  useBus(eventBus, 'submitExistingTarget', submit);
  useBus(eventBus, AdManagerEvents.ENTITY_EVENT_METRICS_GRID_FILTER_UPDATED, sideBarFilterUpdated);

  const handleSortClick = (field: string) => {
    let newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    if (sortFieldName !== field) {
      newSortDirection = 'desc';
    }
    // @ts-ignore
    setSortDirection(newSortDirection);
    setSortFieldName(field);
  };

  const columnDef = [
    {
      headerName: 'Product',
      field: 'stacklineSku',
      width: undefined,
      enableRtl: true,
      cellStyle: { 'justify-content': 'flex-start', 'text-align': 'left' },
      minWidth: 300,
      maxWidth: 800,
      cellRendererFramework: CampaignColumn,
      headerClass: 'align-left'
    },
    {
      headerName: 'Bid',
      cellRendererFramework: EditBitColumn,
      field: 'bid',
      minWidth: 110,
      maxWidth: 110,
      cellStyle: { 'justify-content': 'flex-start', 'text-align': 'left' },
      headerClass: 'align-left',
      disableSort: false,
      sortDirection,
      sortByMetric: sortFieldName,
      headerComponentFramework: CustomAgGridHeaderTemplate,
      onSortFunction: () => {
        handleSortClick('bid');
      }
    },
    {
      headerName: 'Ad Clicks',
      cellRendererFramework: DataColumn,
      field: 'clicks',
      minWidth: 130,
      maxWidth: 130,
      cellStyle: { 'justify-content': 'flex-end', 'text-align': 'right', 'padding-right': '20px' },
      disableSort: false,
      sortDirection,
      sortByMetric: sortFieldName,
      headerComponentFramework: CustomAgGridHeaderTemplate,
      onSortFunction: () => {
        handleSortClick('clicks');
      }
    },
    {
      headerName: 'Ad Spend',
      cellRendererFramework: DataColumn,
      field: 'spend',
      cellStyle: { 'justify-content': 'flex-end', 'text-align': 'right', 'padding-right': '20px' },
      disableSort: false,
      sortDirection,
      sortByMetric: sortFieldName,
      headerComponentFramework: CustomAgGridHeaderTemplate,
      onSortFunction: () => {
        handleSortClick('spend');
      }
    },
    {
      headerName: 'Ad Sales',
      cellRendererFramework: DataColumn,
      field: 'sales',
      cellStyle: { 'justify-content': 'flex-end', 'text-align': 'right', 'padding-right': '20px' },
      sortDirection,
      sortByMetric: sortFieldName,
      disableSort: false,
      headerComponentFramework: CustomAgGridHeaderTemplate,
      onSortFunction: () => {
        handleSortClick('sales');
      }
    },
    {
      headerName: 'ROAS',
      cellRendererFramework: DataColumn,
      field: 'returnOnAdSpend',
      cellStyle: { 'justify-content': 'flex-end', 'text-align': 'right', 'padding-right': '20px' },
      sortDirection,
      sortByMetric: sortFieldName,
      disableSort: false,
      headerComponentFramework: CustomAgGridHeaderTemplate,
      onSortFunction: () => {
        handleSortClick('returnOnAdSpend');
      }
    },
    {
      headerName: 'CPC',
      cellRendererFramework: DataColumn,
      field: 'costPerClick',
      cellStyle: { 'justify-content': 'flex-end', 'text-align': 'right', 'padding-right': '20px' },
      sortDirection,
      sortByMetric: sortFieldName,
      disableSort: false,
      headerComponentFramework: CustomAgGridHeaderTemplate,
      onSortFunction: () => {
        handleSortClick('costPerClick');
      }
    }
  ];

  const fetchData = async () => {
    setLoading(true);
    const indexName = 'adCampaignAdGroupProductDailyMetrics';
    const pageSize = 1000;
    const statePropertyName = `existingProductForCampaign_${mainEntity.id}`;
    const widget = {
      view: {
        gridOptions: { pageSize }
      },
      data: { statePropertyName }
    };
    const initAggregationFields = [
      'clicks',
      'spend',
      'sales',
      'unitsSold',
      'costPerClick',
      'returnOnAdSpend',
      'costPerAcquisition',
      'adCostOfSales'
    ].map((fieldName) => INDEX_FIELDS.getField(app.name, indexName, fieldName));

    const [{ aggregations: aggregationFields }] = buildAggregations(initAggregationFields);
    const { mainTimePeriodRangeFilters, comparisonTimePeriodRangeFilters } = buildTimePeriodRangeFilters({
      app,
      indexName,
      mainTimePeriod,
      comparisonTimePeriod
    });
    const termFilters = [
      {
        fieldName: 'campaignId',
        values: [mainEntity.campaignId]
      },
      {
        fieldName: 'adGroupId',
        values: [adGroupId]
      }
    ];

    const searchSideBarConditions = {
      termFilters: [...termFilters],
      rangeFilters: [...mainTimePeriodRangeFilters]
    };

    terms.forEach(({ filterName, fieldName }: { filterName: string; fieldName: string }) => {
      if (_get(formData, ['termFilters', filterName])) {
        searchSideBarConditions.termFilters = [
          ...searchSideBarConditions.termFilters,
          ...buildTermFilterCondition({
            filterValues: formData.termFilters[filterName].values,
            condition: formData.termFilters[filterName].condition,
            idFieldName: fieldName
          })
        ];
      }
    });

    const aggregationFilters = {
      termFilters: [],
      rangeFilters: [],
      computeFilters: []
    };

    const emptyFormData = getEmptyFormData({ appName: AppName.Advertising });

    ranges.forEach(({ filterName, fieldName }: { filterName: string; fieldName: string }) => {
      if (_get(formData, ['rangeFilters', filterName])) {
        if (isRangeFilterNotEqual(emptyFormData.rangeFilters[filterName], formData.rangeFilters[filterName])) {
          if (filterName !== 'bid') {
            aggregationFilters.computeFilters = [
              ...aggregationFilters.computeFilters,
              {
                fieldName,
                minValue: formData.rangeFilters[filterName].minValue,
                maxValue: formData.rangeFilters[filterName].maxValue
              }
            ];
          }
        }
      }
    });

    const searchRequestOverrides = [
      {
        doAggregation: true,
        aggregations: [
          {
            aggregationFields,
            conditions: {
              termFilters: [
                { fieldName: 'retailerId', values: [Number.parseInt(retailer.id as any, 10)] },
                {
                  fieldName: 'isProjected',
                  condition: 'must',
                  values: [0]
                }
              ],
              rangeFilters: [...mainTimePeriodRangeFilters],
              computeFilters: [...aggregationFilters.computeFilters]
            },
            sortDirection,
            sortByAggregationField: aggregationFields.find((x) => x.aggregateByFieldName === sortFieldName),
            comparisonRangeFilters: comparisonTimePeriodRangeFilters,
            groupByFieldName: 'stacklineSku'
          }
        ],
        conditions: searchSideBarConditions,
        pageSize,
        processDocuments: false
      }
    ];

    const result = await store.dispatch(
      fetchEntityMetrics(
        statePropertyName,
        {
          entity: mainEntity,
          retailer,
          app,
          indexName,
          customResponseParser: (action: any) => zipMetricsResponseIntoArray(action, widget)
        },
        searchRequestOverrides,
        null,
        true
      )
    );

    const cloneResult = result;

    const isAutoTargeting = _get(mainEntity, ['extendedAttributes', 'amsApiModel', 'targetingType']) === 'auto';

    const showZeroProducts = (retailer.id === '2' || retailer.id === '25') && isAutoTargeting;

    const stacklineSkuValues = result.data.map((dataPoint: any) => dataPoint.id);
    const queryConditions = {
      termFilters: [
        {
          fieldName: 'stacklineSku',
          condition: 'should',
          values: showZeroProducts ? [] : stacklineSkuValues
        }
      ],
      rangeFilters: []
    };

    const { documents: adCampaignProductDocuments } = await store.dispatch(
      fetchEntityMetrics(
        `${statePropertyName}_Documents`,
        {
          entity: mainEntity,
          retailer,
          app,
          indexName: 'adCampaignAdGroupProduct'
        },
        [
          {
            doAggregation: false,
            conditions: {
              termFilters: [...searchSideBarConditions.termFilters, ...queryConditions.termFilters],
              rangeFilters: []
            },
            pageNumber: 1,
            pageSize: 10000,
            processDocuments: false,
            returnDocuments: true
          }
        ],
        null,
        true
      )
    );

    const productDocs = adCampaignProductDocuments.map((doc: any) => {
      const stacklineSku = _get(doc, ['stacklineSku'], '');
      const bid = _get(doc, ['cpcMaxBidAmount']);
      return {
        stacklineSku,
        bid,
        entity: doc
      };
    });

    if (showZeroProducts && !cloneResult.hasMoreRows) {
      const skusWithData = cloneResult.data.map((item: any) => item.entity.stacklineSku);
      const isIncludeInResult = (sku: string) => {
        return skusWithData.includes(sku);
      };
      const insertZeroProduct = (product: any) => {
        const productEntity = product.entity;

        if (!cloneResult.data.find((item: any) => item.id === product.stacklineSku.toLowerCase())) {
          cloneResult.data.push({
            entity: {
              ...productEntity,
              name: productEntity.title,
              type: 'product'
            }
          });
        }
      };
      productDocs.forEach((product: any) => {
        if (!isIncludeInResult(product.stacklineSku)) {
          insertZeroProduct(product);
        }
      });
    }

    const fullResult: any[] = cloneResult.data.map((dataPoint: any) => {
      const stacklineSku = _get(dataPoint, ['entity', 'stacklineSku']);
      const document = productDocs.find((doc: any) => doc.stacklineSku === stacklineSku);
      return {
        ...dataPoint,
        id: stacklineSku.toLowerCase(),
        bid: _get(document, 'bid'),
        originalBid: _get(document, 'bid'),
        entity: {
          ...dataPoint.entity,
          ...document.entity,
          id: stacklineSku
        }
      };
    });

    let cleanedAndOptionallySortedData = [];

    cleanedAndOptionallySortedData = _cloneDeep(fullResult);
    if (sortFieldName === 'bid') {
      cleanedAndOptionallySortedData = _orderBy(cleanedAndOptionallySortedData, 'bid', sortDirection);
    } else {
      cleanedAndOptionallySortedData = _orderBy(
        cleanedAndOptionallySortedData,
        (e) => _get(e, [sortFieldName, 'value'], 0),
        sortDirection
      );
    }

    // since we cannot filter first call by bid, we filter on second call so we must filter out those
    // datapoints without a bid (meaning they did not fall within the range in the 2nd call)
    cleanedAndOptionallySortedData = cleanedAndOptionallySortedData.filter(
      (dataPoint: any) => dataPoint.bid !== undefined
    );

    eventBus.emit('setSearchHeader', {
      isLoading: false,
      entity: { result: cleanedAndOptionallySortedData }
    });
    setDataSet(cleanedAndOptionallySortedData);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mainEntity, mainTimePeriod, retailer.id, adGroupId, sortDirection, sortFieldName]);

  const renderGrid = () => {
    return (
      <div className="recommendations_grid" style={{ position: 'relative' }}>
        <CustomAgMaterial
          onGridReady={onGridReady}
          onCellValueChanged={onGridReady}
          onModelUpdated={onGridReady}
          onRowValueChanged={onGridReady}
          onRowDataChanged={onGridReady}
          buildRows={() => dataSet}
          suppressNoRowsOverlay
          columnDefs={columnDef}
          getRowNodeId={(datum: any) => escape(datum.id)}
          domLayout="autoHeight"
          rowHeight={120}
          containerStyle={{ width: '100%' }}
        />
      </div>
    );
  };

  return <div className="targeting_container">{isLoading ? <GridLoading /> : renderGrid()}</div>;
};

export default connect(mapStateToProps)(withBus('eventBus')(ExistingTargets));
